import { Box, ButtonBase, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Fixed height of a single day cell / week row. Fixed (rather than
 * width-driven via aspect-ratio) so the calendar stays compact and does not
 * balloon vertically on the wider desktop sheet — which previously pushed the
 * bottom sheet past 90vh on short screens and triggered a scrollbar (MC-11243).
 */
const DAY_CELL_SIZE = 44;

/** Weeks reserved so the grid height stays constant across 4-, 5- and 6-row months. */
const CALENDAR_ROWS = 6;

export const CalendarWrapper = styled(Box)(({ theme }) => ({
	padding: theme.spacing(2),
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(1),
}));

export const CalendarHeader = styled(Stack)({
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'space-between',
});

export const MonthLabel = styled(Stack)(({ theme }) => ({
	flexDirection: 'row',
	alignItems: 'center',
	gap: theme.spacing(0.5),
	cursor: 'default',
}));

export const WeekdayRow = styled(Box)({
	display: 'grid',
	gridTemplateColumns: 'repeat(7, 1fr)',
	textAlign: 'center',
});

export const DayGrid = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: 0,
	// Reserve 6 rows so the calendar height stays constant across 4-, 5- and
	// 6-row months, using a fixed row height (see DAY_CELL_SIZE) rather than a
	// width-driven aspect ratio that grew too tall on the wide desktop sheet.
	minHeight: DAY_CELL_SIZE * CALENDAR_ROWS,
});

export const WeekRow = styled(Box)({
	display: 'grid',
	gridTemplateColumns: 'repeat(7, 1fr)',
});

interface IDayCellProps {
	ownerState: {
		disabled: boolean;
	};
}

export const DayCell = styled(ButtonBase, {
	shouldForwardProp: (prop) => prop !== 'ownerState',
})<IDayCellProps>(({ ownerState }) => {
	const { disabled } = ownerState;

	return {
		width: '100%',
		height: DAY_CELL_SIZE,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		cursor: disabled ? 'default' : 'pointer',
		pointerEvents: disabled ? 'none' : 'auto',
		backgroundColor: 'transparent',
	};
});

interface IDayCircleProps {
	ownerState: {
		isSelected: boolean;
		isToday: boolean;
		inRange: boolean;
		disabled: boolean;
	};
}

export const DayCircle = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'ownerState',
})<IDayCircleProps>(({ theme, ownerState }) => {
	const { isSelected, isToday, inRange, disabled } = ownerState;

	const backgroundColor = isSelected
		? theme.palette.ticketing?.main ?? theme.palette.primary.main
		: inRange
		? theme.palette.ticketing?.[100] ?? theme.palette.primary[100]
		: 'transparent';

	const color = isSelected
		? theme.palette.ticketing?.contrastText ?? theme.palette.common.white
		: disabled
		? theme.palette.neutral?.[300] ?? theme.palette.text.disabled
		: theme.palette.text.primary;

	const border =
		isToday && !isSelected
			? `1.5px solid ${
					theme.palette.ticketing?.main ?? theme.palette.primary.main
			  }`
			: 'none';

	return {
		width: 32,
		height: 32,
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxSizing: 'border-box',
		fontSize: theme.typography.body2.fontSize,
		fontFamily: theme.typography.fontFamily,
		backgroundColor,
		color,
		border,
	};
});

export const EmptyCell = styled(Box)({
	width: '100%',
	height: DAY_CELL_SIZE,
});

export const FooterRow = styled(Stack)(({ theme }) => ({
	flexDirection: 'row',
	justifyContent: 'flex-end',
	gap: theme.spacing(1),
	paddingTop: theme.spacing(1),
}));
